/**
 * CountrySelect
 *
 * Searchable country dropdown with flag emoji, country name (i18n), and dial code.
 * Value is the ISO 3166-1 alpha-2 code (e.g. "ES").
 *
 * Usage:
 *   <CountrySelect value={value} onChange={setValue} />
 *   <CountrySelect value={value} onChange={setValue} showDialCode />
 */

import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useTranslation } from '@/core';
import { Button } from '@/theme/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/theme/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/theme/components/ui/popover';
import { cn } from '@/lib/utils';
import { COUNTRIES, getCountry } from '@/shared/constants/countries';

interface CountrySelectProps {
  value?: string;
  onChange: (code: string) => void;
  placeholder?: string;
  showDialCode?: boolean;
  disabled?: boolean;
  className?: string;
}

export function CountrySelect({
  value,
  onChange,
  placeholder,
  showDialCode = false,
  disabled = false,
  className,
}: CountrySelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const selected = value ? getCountry(value) : undefined;
  const defaultPlaceholder = t('common.selectCountry', 'Select country...');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full justify-between font-normal',
            !selected && 'text-muted-foreground',
            className,
          )}
        >
          <span className="flex items-center gap-2 truncate">
            {selected ? (
              <>
                <span className="text-base leading-none">{selected.flag}</span>
                <span className="truncate">
                  {t(`country.${selected.code}`, selected.nameEn)}
                  {showDialCode && (
                    <span className="ml-1 text-muted-foreground text-xs">
                      {selected.dialCode}
                    </span>
                  )}
                </span>
              </>
            ) : (
              placeholder ?? defaultPlaceholder
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput
            placeholder={t('common.searchCountry', 'Search country...')}
          />
          <CommandList>
            <CommandEmpty>
              {t('common.noCountryFound', 'No country found.')}
            </CommandEmpty>
            <CommandGroup>
              {COUNTRIES.map((country) => {
                const label = t(`country.${country.code}`, country.nameEn);
                return (
                  <CommandItem
                    key={country.code}
                    value={`${label} ${country.code} ${country.dialCode}`}
                    onSelect={() => {
                      onChange(country.code);
                      setOpen(false);
                    }}
                  >
                    <span className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-base leading-none shrink-0">
                        {country.flag}
                      </span>
                      <span className="truncate">{label}</span>
                      {showDialCode && (
                        <span className="ml-auto text-xs text-muted-foreground shrink-0">
                          {country.dialCode}
                        </span>
                      )}
                    </span>
                    <Check
                      className={cn(
                        'ml-2 h-4 w-4 shrink-0',
                        value === country.code ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
